import React from 'react';
import { NavLink } from 'react-router-dom';
import './Legal.css';

function Terms() {
  return (
    <main className="legal-container">
      <nav className="legal-tabs" aria-label="Legal pages">
        <NavLink to="/privacy-policy" className="legal-tab">Privacy Policy</NavLink>
        <NavLink to="/terms" className={({ isActive }) => isActive ? 'legal-tab active' : 'legal-tab'}>Terms and Conditions</NavLink>
        <NavLink to="/about" className="legal-tab">About Us</NavLink>
        <NavLink to="/contact" className="legal-tab">Contact</NavLink>
      </nav>
      <header className="legal-header">
        <p className="legal-eyebrow">SponsorBoat legal</p>
        <h1>Terms and Conditions</h1>
        <p>Last updated: August 22, 2026</p>
      </header>

      <article className="legal-content">
        <p>
          These Terms and Conditions govern your access to and use of SponsorBoat, a platform for
          discovering and presenting creator sponsorship opportunities. By using SponsorBoat,
          creating an account, submitting a listing, or purchasing a subscription, you agree to
          these terms. If you do not agree, please do not use the service.
        </p>

        <section>
          <h2>1. Eligibility and Accounts</h2>
          <p>
            You must be legally able to enter into these terms and provide accurate information.
            Keep your account credentials confidential and notify us promptly if you suspect
            unauthorized access. You are responsible for activity performed through your account.
            One person must not create accounts to evade a suspension or misuse the service.
          </p>
        </section>

        <section>
          <h2>2. SponsorBoat Services</h2>
          <p>
            SponsorBoat provides tools for browsing creator listings, submitting creator profiles,
            and connecting sponsors with creators. We may review, approve, reject, edit for
            formatting, limit, or remove listings and may change or discontinue features as the
            service develops. A listing or account approval does not mean that SponsorBoat endorses
            a creator, sponsor, campaign, product, or result.
          </p>
        </section>

        <section>
          <h2>3. Creator Submissions</h2>
          <p>
            You are responsible for ensuring that your listing information, photos, links, metrics,
            and contact details are accurate and current. You represent that you own or have
            permission to submit and display the content, and that it does not violate another
            person's privacy, publicity, intellectual property, or other rights.
          </p>
          <p>
            By submitting content, you give SponsorBoat a non-exclusive, worldwide, royalty-free
            license to host, store, reproduce, format, display, and promote that content as needed
            to operate and market the service. You keep ownership of your content. This license
            ends for content removed from the service, except where retention is needed for legal,
            security, backup, or dispute-resolution purposes.
          </p>
        </section>

        <section>
          <h2>4. Acceptable Use</h2>
          <p>You must not:</p>
          <ul>
            <li>Submit false, misleading, stolen, infringing, abusive, unlawful, or harmful content.</li>
            <li>Impersonate a person or organization or misrepresent audience metrics or business relationships.</li>
            <li>Use contact information for spam, harassment, phishing, fraud, or unwanted bulk messaging.</li>
            <li>Upload malware or attempt to disrupt, probe, scrape, reverse engineer, or bypass security controls.</li>
            <li>Use the service to violate applicable laws or another person's rights.</li>
          </ul>
        </section>

        <section>
          <h2>5. Sponsor and Creator Relationships</h2>
          <p>
            SponsorBoat is a discovery and introduction platform, not a party to agreements between
            sponsors and creators. Users are responsible for evaluating opportunities, verifying
            claims, negotiating deliverables and compensation, complying with advertising and
            disclosure laws, and resolving disputes directly. We do not guarantee sponsorships,
            campaign performance, payment, audience quality, or the accuracy of third-party listings.
          </p>
        </section>

        <section>
          <h2>6. Subscriptions and Payments</h2>
          <p>
            Some browsing features require an active subscription. Prices, plan duration, currency,
            taxes, and payment terms are shown at checkout. Subscription access begins after
            successful payment confirmation and may be limited, suspended, or ended if payment is
            reversed, fraudulent, or otherwise unsuccessful. Payments are processed by the payment
            provider shown at checkout and may be subject to its terms.
          </p>
          <p>
            Unless required by applicable law or expressly stated at checkout, subscription fees
            are non-refundable. Contact us promptly if you believe a payment or subscription record
            is incorrect. We may change pricing or plans prospectively by posting updated details.
          </p>
        </section>

        <section>
          <h2>7. Intellectual Property</h2>
          <p>
            SponsorBoat's software, branding, design, text, and service features belong to
            SponsorBoat or its licensors and are protected by applicable intellectual property
            laws. These terms give you a limited, personal, non-transferable right to use the
            service for its intended purpose. You may not copy, resell, reproduce, or create a
            competing service from SponsorBoat materials without written permission.
          </p>
        </section>

        <section>
          <h2>8. Third-Party Links and Services</h2>
          <p>
            Listings may link to YouTube, Instagram, blogs, payment providers, or other third-party
            services. Those services are controlled by their respective providers and are governed
            by their own terms and privacy policies. SponsorBoat is not responsible for their
            availability, content, security, or practices.
          </p>
        </section>

        <section>
          <h2>9. Disclaimers</h2>
          <p>
            To the extent permitted by law, SponsorBoat is provided on an "as is" and "as
            available" basis. We do not promise that the service will always be uninterrupted,
            error-free, secure, or suitable for a particular purpose. Information on the platform
            is provided for discovery and general information, not legal, financial, marketing, or
            professional advice.
          </p>
        </section>

        <section>
          <h2>10. Liability and Indemnity</h2>
          <p>
            To the extent permitted by law, SponsorBoat and its operators will not be liable for
            indirect, incidental, special, consequential, or lost-profit damages arising from your
            use of the service or your relationship with another user. You agree to defend and hold
            SponsorBoat harmless from claims arising from your content, misuse of the service,
            violation of these terms, or violation of another person's rights. Nothing in these
            terms excludes liability that cannot legally be excluded.
          </p>
        </section>

        <section>
          <h2>11. Suspension and Termination</h2>
          <p>
            You may stop using SponsorBoat at any time. We may suspend or terminate access, remove
            content, or cancel an account when reasonably necessary to protect users, investigate
            abuse, enforce these terms, comply with law, or operate the service. Provisions that
            should continue by their nature, including intellectual property, disclaimers,
            liability, and dispute terms, will survive termination.
          </p>
        </section>

        <section>
          <h2>12. Changes and General Terms</h2>
          <p>
            We may update these terms by posting a revised version and changing the date above.
            Continued use after an update means you accept the revised terms. If a provision is
            found unenforceable, the remaining provisions remain in effect. These terms and the
            Privacy Policy are the complete agreement about your use of SponsorBoat unless we state
            otherwise in writing.
          </p>
        </section>

        <section>
          <h2>13. Contact</h2>
          <p>
            Questions about these terms should be sent through the <a href="/contact">Contact page</a>.
          </p>
        </section>
      </article>
    </main>
  );
}

export default Terms;
